/**
 * `/templates/:templateId` — the block editor and the preview.
 *
 * The shape of this screen follows the shape of the document. `templates.blocks`
 * is a run of body blocks ending in exactly one footer, expressed in
 * `@wend/shared` as the tuple `[...BodyBlock[], FooterBlock]`, and the draft here
 * holds those two halves as two separate pieces of state. That is not a
 * stylistic choice: it means "delete the footer" and "move a block below the
 * footer" are not operations this editor refuses, they are operations it cannot
 * express. The rule that keeps a tenant's own AWS account alive is enforced by
 * the shape of the state rather than by a check somebody can forget.
 *
 * **The preview is the API's answer, debounced.** Typing does not compile
 * anything; it schedules a request, and one request goes out per pause. The same
 * request carries back the approval blockers and the variables, so the editor and
 * the API can never disagree about whether a template is finished — there is only
 * one opinion and it is the one that also decides whether the status endpoint
 * allows the transition.
 *
 * **A document that cannot compile is caught before the round trip.** The
 * request is parsed against the shared preview contract first, so `{{ compnay }}`
 * or a `javascript:` link is refused here, with the contract's own message,
 * against the block that caused it — and the last preview that *did* compile
 * stays on screen, labelled as such, rather than the frame going blank.
 *
 * **Slice 11 added a second read, and it is here rather than in each panel.**
 * This editor used to state slice 4's promise as a fact — *an email Wend sends
 * makes no network request when it is opened* — in the preview, beside the test
 * send and in the reasoning for why an image block references an upload rather
 * than a URL. With tracking on, Amazon injects a pixel and rewrites every link
 * after Wend has handed the message over, so that sentence is now true for some
 * workspaces and false for others and has to be **read** rather than printed.
 * (Slice 21 made it depend on the deployment too: where pictures are hosted, an
 * email with a picture fetches it when opened. `whatTheMessageCarries` is handed
 * `template.imageDelivery` for that.)
 *
 * `GET /tracking` is fetched once per editor and the answer is passed down as
 * `TrackingKnowledge`. One read, not three, for the reason the Overview gives
 * for its own single request: three panels each fetching their own settings can
 * land in three different states and describe the same message three different
 * ways on one screen. It is fetched here rather than in `TemplateEditorPage`
 * above so that opening a *WhatsApp* template asks nothing — tracking is an
 * email configuration set, and there is nothing for it to say about a WhatsApp
 * message.
 *
 * A failed read does not fall back to the reassuring sentence. See
 * `lib/recipient-experience.ts`: "we could not read this" is its own state with
 * its own words, because defaulting to *off* would be right for most workspaces
 * and wrong for exactly the ones the sentence can hurt.
 *
 * ## Slice 17 — what was cut from this screen, and the two things that were not
 *
 * The settings card described what a subject and a preheader *are* and that both
 * are checked for variables at save; the delete panel explained that a
 * confirmation's job is to say what is lost rather than to ask whether you are
 * sure. Both were written for whoever picked up the next slice, and both are
 * here now rather than on the screen. What the fields keep is the part a person
 * acts on: which are optional, and what a client does with an empty preheader.
 *
 * **The approved-template warning stayed, and it is the reason the rule has an
 * exception at all.** A template that a campaign has already been built on is
 * edited in place — there is no copy-on-approve — so editing it changes what
 * that campaign will send, silently, and no other screen in the product says so.
 * A reader who never opens a disclosure would draw the opposite conclusion and
 * find out when the campaign goes out. It is two sentences instead of three and
 * it is still the first thing on the tab.
 *
 * ## Pictures with no public address, where this deployment hosts pictures
 *
 * The template view carries `imageDelivery`. Where it is `hosted`, a picture on
 * the document whose asset has no `publicId` would be a broken image in every
 * inbox, and a campaign on this template is refused at schedule time
 * (`template_images_not_hostable`) with a sentence telling the person to open
 * the template and save it. **That instruction has to be a button that works.**
 * Saving is what publishes such a picture, but Save is disabled on a template
 * with no unsaved edits — so a person following the instruction would find
 * nothing to press, and the only way through would be a pointless edit. So,
 * for exactly that case, Save stays enabled, says what it will do, and the same
 * shared sentence the campaign refusal uses is shown above the tabs. That
 * sentence counts; the image card of each picture with no address marks itself
 * (`BlockEditor`), which is what lets "remove that picture and upload it again"
 * name one picture rather than all of them. Where the deployment embeds
 * pictures a null `publicId` is normal and nothing is said.
 *
 * **And the delete confirmation now names what it destroys in the sentence
 * itself** — the template, its compiled email and its uploads — rather than
 * naming them in a card description two paragraphs above the button and asking
 * "are you sure?" beside it. A campaign built on it later is a consequence, not
 * a thing being deleted, so it is no longer in the confirmation.
 *
 * ## Slice 56 — width and background are template settings, not block ones
 *
 * The owner asked for two things about the email as a whole: to lose the 557px
 * column ("so that the mail body and image can take the full width") and to
 * lose the white ("keep background transparent"). They are two columns on the
 * template — `bodyWidth` and `background` — so they live in the draft beside
 * the subject and the reply-to, are edited on the settings card, and travel on
 * both roads to the server: the save, which recompiles `html_compiled`, and the
 * preview request, which compiles the unsaved choice. The frame is drawn from
 * the settings the HTML inside it was compiled for, kept beside the preview's
 * answer when it arrives — not from the latest request, which is built before
 * its answer arrives and may never get one. Until the new HTML lands, the old
 * HTML keeps its old width and background. Per-block width or background was
 * out of scope and would have been a second answer to "how wide is this
 * email".
 *
 * **The defaults are today's email, byte for byte** — 'standard' and 'white'
 * compile to exactly what every template compiled to before the columns
 * existed. That is the API's promise; this screen's half of it is to send the
 * template's own values back unchanged when nobody touches the selects, which
 * is what `draftOf` does.
 *
 * The settings card lost its description, "What an inbox shows before the
 * message is opened." It was written for a card holding the subject and the
 * preheader; a card that also holds the width and the background of the opened
 * message made it false, and the card's fields are labelled.
 *
 * **Dirty-checking now compares the draft by content, not by key order.** A
 * formatted paragraph arrives from the API with `runs` wherever the API's
 * schema puts it and leaves the editor with `runs` wherever `withRuns` puts it;
 * `JSON.stringify` would call two identical paragraphs different and leave
 * "unsaved changes" lit on a template nobody changed. `signature`, in
 * `lib/template-draft.ts` with the rest of the draft, sorts keys.
 */
import {
  fieldErrorsFromZod,
  templateAttachmentsUnpublishedMessage,
  templateImagesUnpublishedMessage,
  templatePreviewRequestSchema,
  updateEmailTemplateRequestSchema,
  type Template,
  type TemplateAsset,
  type TemplatePreviewRequest,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  buttonVariants,
  useToast,
} from '@wend/ui';
import { ArrowLeft, CheckCircle2, Eye, PencilLine, Save, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { TemplateStatusBadge } from '../../components/TemplateStatusBadge';
import { Textarea } from '../../components/Textarea';
import { errorMessage, isApiError } from '../../lib/api/errors';
import {
  deleteTemplate,
  fetchTemplate,
  previewTemplate,
  setTemplateStatus,
  updateTemplate,
} from '../../lib/api/templates';
import { fetchTracking } from '../../lib/api/tracking';
import { formatDateTime } from '../../lib/format';
import {
  documentOf,
  draftOf,
  previewCandidateOf,
  saveRequestOf,
  signature,
  type EmailTemplateDraft,
} from '../../lib/template-draft';
import { trackingKnowledge, whatTheMessageCarries } from '../../lib/recipient-experience';
import {
  NO_TEMPLATE_ISSUES,
  TEMPLATE_BACKGROUND_OPTIONS,
  TEMPLATE_BODY_WIDTH_OPTIONS,
  TEMPLATE_STATUS_DISPLAY,
  fieldIssues,
  isTemplateBackground,
  isTemplateBodyWidth,
  mergeIssues,
  routeTemplateIssues,
  type TemplateIssues,
} from '../../lib/template-display';
import { useAsyncData } from '../../lib/use-async-data';
import { AttachmentsPanel } from './AttachmentsPanel';
import { RecipientTrackingNotice } from './RecipientTrackingNotice';
import { TestSendPanel } from './TestSendPanel';
import { BlockEditor, VariableHelp } from './BlockEditor';
import { TemplatePreview } from './TemplatePreview';
import { WaTemplateEditorPage } from './whatsapp/WaTemplateEditorPage';

/**
 * How long typing pauses before a preview is requested.
 *
 * Longer than the 300ms a list search waits, because this request compiles an
 * email rather than running a query, and a preview that lands half a second
 * after you stop typing feels immediate anyway.
 */
const PREVIEW_DEBOUNCE_MS = 600;

export function TemplateEditorPage() {
  const { templateId = '' } = useParams<{ templateId: string }>();

  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchTemplate(templateId, signal),
    [templateId],
  );

  if (data === null) {
    return (
      <AppLayout title="Template" actions={<BackToTemplates />}>
        <Card>
          {loadingFirst ? (
            <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading this template…</span>
            </CardContent>
          ) : (
            <ErrorState error={error} title="Could not load this template" onRetry={reload} />
          )}
        </Card>
      </AppLayout>
    );
  }

  // Two channels, one route. The list links to `/templates/:id` without knowing
  // which kind of document is behind it, and the type on the loaded template is
  // what decides — rather than a second URL the list would have to guess at.
  if (data.type === 'whatsapp') {
    return <WaTemplateEditorPage key={data.id} loaded={data} onReload={reload} />;
  }

  // Keyed by id so that opening a different template starts a fresh draft, and
  // *only* by id so that a reload of the same one does not throw away edits.
  return <TemplateEditor key={data.id} loaded={data} />;
}

function BackToTemplates() {
  return (
    <Link to="/templates" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
      <ArrowLeft aria-hidden="true" className="size-4" />
      All templates
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* The draft                                                                   */
/* -------------------------------------------------------------------------- */

/*
 * The draft's shape, the two requests built from it and `signature` live in
 * `lib/template-draft.ts` since slice 56 — moved so a verify suite can call
 * them, which it cannot do to anything in this file. See that file's header.
 */
type Draft = EmailTemplateDraft;

/* -------------------------------------------------------------------------- */
/* The editor                                                                  */
/* -------------------------------------------------------------------------- */

function TemplateEditor({ loaded }: { loaded: Template }) {
  const navigate = useNavigate();
  const { toast } = useToast();

  /** The server's copy. Replaced by whatever a save, an approval or a load says. */
  const [template, setTemplate] = useState<Template>(loaded);
  const [draft, setDraft] = useState<Draft>(() => draftOf(loaded));
  const [substitute, setSubstitute] = useState(true);

  /**
   * The inline images this editor knows about: the ones the template arrived
   * with, plus anything uploaded since. The upload endpoint answers with the
   * asset it just made, so a fresh one can be shown without re-reading the
   * template.
   */
  const [assets, setAssets] = useState<readonly TemplateAsset[]>(loaded.assets);

  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [saveIssues, setSaveIssues] = useState<TemplateIssues>(NO_TEMPLATE_ISSUES);
  const [saveFailure, setSaveFailure] = useState<{
    message: string;
    requestId: string | undefined;
  } | null>(null);

  const savedSignature = useMemo(() => signature(draftOf(template)), [template]);
  const dirty = signature(draft) !== savedSignature;

  /*
   * Pictures on the document that a hosting deployment has no address for yet.
   *
   * Counted as DISTINCT assets the document references — the same unit the
   * campaign refusal counts, so the two sentences agree about "2 of the 3" — and
   * only where `imageDelivery === 'hosted'`, compared with `===` so a null mode
   * reads as "nothing to publish" rather than as the reverse. An asset id the
   * editor has no row for is not counted here: that is a missing upload, a
   * different fault the save itself refuses by name.
   */
  const imagePublishing = useMemo(() => {
    if (template.imageDelivery !== 'hosted') return { referenced: 0, unpublished: 0 };
    const referenced = [
      ...new Set(draft.body.filter((block) => block.type === 'image').map((block) => block.assetId)),
    ];
    const unpublished = referenced.filter((assetId) =>
      assets.some((asset) => asset.id === assetId && asset.publicId === null),
    ).length;
    return { referenced: referenced.length, unpublished };
  }, [template.imageDelivery, draft.body, assets]);

  /*
   * THE SAME COUNT FOR FILES, AND THE REASON IT HAS TO EXIST.
   *
   * `template_attachments_not_linkable` refuses a campaign with "Open the
   * template and save it: saving publishes every file that is missing an
   * address." Without this, that instruction could not be carried out: a file is
   * referenced by no block, so uploading or removing one never makes the editor
   * dirty, and Save would read "Saved" and be disabled on the very screen the
   * refusal sent the person to. Their only escape would be a dummy edit.
   *
   * Every attachment on the template, not a referenced subset — nothing
   * references an attachment, which is the whole difference between the two
   * kinds. `=== 'hosted'` for the same reason the picture count uses it: where
   * files are attached a null `publicId` is how every row is.
   */
  const attachmentPublishing = useMemo(() => {
    if (template.imageDelivery !== 'hosted') return { total: 0, unpublished: 0 };
    const attachments = assets.filter((asset) => asset.kind === 'attachment');
    return {
      total: attachments.length,
      unpublished: attachments.filter((asset) => asset.publicId === null).length,
    };
  }, [template.imageDelivery, assets]);

  const needsPicturePublishing = imagePublishing.unpublished > 0;
  const needsFilePublishing = attachmentPublishing.unpublished > 0;
  const needsPublishing = needsPicturePublishing || needsFilePublishing;

  /* ---- what this workspace's mail actually carries ----------------------- */

  /**
   * Read once, with no dependencies: tracking belongs to the workspace, not to
   * the template, so editing, saving and switching tabs must not re-ask.
   *
   * There is no error branch and no retry button on purpose. The three panels
   * that use it each say what they can and cannot claim, and a failed read
   * produces a sentence rather than an empty space — `trackingKnowledge` is what
   * keeps "we have not asked yet", "we asked and could not find out" and "here
   * is the answer" from collapsing into one another.
   */
  const trackingRead = useAsyncData((signal) => fetchTracking(signal), []);
  const tracking = trackingKnowledge(trackingRead);
  const carriesNotice = whatTheMessageCarries(tracking, template.imageDelivery);

  /* ---- the preview request ---------------------------------------------- */

  /**
   * The last request the contract accepted. It is seeded from the loaded
   * template — which is valid by construction, having just been parsed by the
   * API's own read schema — so the first preview goes out immediately instead of
   * waiting out a debounce on a document nobody has touched.
   */
  const [previewRequest, setPreviewRequest] = useState<TemplatePreviewRequest>(() => {
    const parsed = templatePreviewRequestSchema.safeParse(previewCandidateOf(draftOf(loaded), true));
    // The fallback previews the *stored* document, which is the one thing that
    // is certainly compilable if the draft somehow is not.
    return parsed.success ? parsed.data : { substituteSampleValues: true };
  });

  /** What the contract refused before a request was made. */
  const [localIssues, setLocalIssues] = useState<TemplateIssues>(NO_TEMPLATE_ISSUES);

  const lastCandidate = useRef<string>(signature(previewCandidateOf(draftOf(loaded), true)));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const candidate = previewCandidateOf(draft, substitute);

      // Editing the name changes the draft and not the email, so the same
      // question is not asked twice.
      const asked = signature(candidate);
      if (asked === lastCandidate.current) return;

      const parsed = templatePreviewRequestSchema.safeParse(candidate);
      if (!parsed.success) {
        // No request: the API would refuse this body, and refusing it here says
        // the same thing sooner and against the same block.
        setLocalIssues(routeTemplateIssues(fieldErrorsFromZod(parsed.error), candidate.blocks));
        return;
      }

      lastCandidate.current = asked;
      setLocalIssues(NO_TEMPLATE_ISSUES);
      setPreviewRequest(parsed.data);
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [draft, substitute]);

  /**
   * The preview, with the settings its request asked for kept beside it. The
   * frame's width and checker are drawn from these, so they change with the
   * HTML they describe and never ahead of it — see "Slice 56" in the header.
   */
  const preview = useAsyncData(
    async (signal) => {
      const settings = {
        bodyWidth: previewRequest.bodyWidth ?? template.bodyWidth,
        background: previewRequest.background ?? template.background,
      };
      const response = await previewTemplate(template.id, previewRequest, signal);
      return { response, ...settings };
    },
    [template.id, previewRequest],
  );

  /* ---- issues ------------------------------------------------------------ */

  const currentDocument = useMemo(() => documentOf(draft), [draft]);

  /**
   * Everything wrong with the document right now, from three places that all
   * speak the same language: what the last save was refused for, what the
   * preview endpoint refused, and what the contract refused before either was
   * asked. Each message is routed to the block that caused it where the path
   * says which, and shown at the top where it does not.
   */
  const previewIssues = useMemo(
    () =>
      preview.error === null || !isApiError(preview.error)
        ? NO_TEMPLATE_ISSUES
        : routeTemplateIssues(preview.error.fieldErrors, currentDocument),
    [preview.error, currentDocument],
  );

  const issues = mergeIssues(mergeIssues(saveIssues, previewIssues), localIssues);

  /* ---- editing ----------------------------------------------------------- */

  function edit(change: Partial<Draft>): void {
    setDraft((current) => ({ ...current, ...change }));
    // The last save's verdict is about a document that no longer exists.
    setSaveIssues(NO_TEMPLATE_ISSUES);
    setSaveFailure(null);
  }

  /* ---- saving ------------------------------------------------------------ */

  async function onSave(): Promise<void> {
    if (saving) return;

    setSaveFailure(null);

    const request = saveRequestOf(draft);

    /*
     * THE EMAIL ARM, NOT `updateTemplateRequestSchema`, AND THE DIFFERENCE IS A
     * SENTENCE A PERSON CAN ACT ON VERSUS THE WORDS "Invalid input".
     *
     * `updateTemplateRequestSchema` is `z.union([whatsapp, email])`. A zod union
     * does not report the failures of its arms: every one of them collapses into
     * a single `invalid_union` issue, at path `[]`, whose message is the literal
     * string "Invalid input". `fieldErrorsFromZod` therefore yields one field
     * error with an empty path, `routeTemplateIssues` cannot pin an empty path on
     * a block so it puts it in `general`, and the screen prints a callout reading
     * **"This template was refused — Invalid input"**. Every carefully written
     * refusal in `@wend/shared` is thrown away one layer earlier than the screen.
     *
     * Hosted images are what made that reachable on the ordinary path rather
     * than a corner: where pictures are hosted, an image block is now added with
     * an empty `alt` on purpose, so the very first Save after uploading a picture
     * is a Save the contract refuses. (Where they are embedded the block is still
     * seeded with the file name, as on `master`, and that first Save succeeds.) `imageBlockSchema` and `BlockEditor`'s add path both state that
     * the editor then shows `TEMPLATE_IMAGE_ALT_REQUIRED_MESSAGE` against the
     * block. Through the union it did not — it showed "Invalid input" at the top
     * of the page and nothing on the block at all until the preview debounce
     * caught up 400 ms later and said something different.
     *
     * This screen is the *email* editor — the WhatsApp composer is
     * `whatsapp/WaTemplateEditorPage.tsx`, and `refuseNonEmail` in
     * `templates.service.ts` refuses the other channel on this endpoint anyway —
     * so it knows which arm it is writing and there is nothing here to
     * discriminate. Parsing the arm gives back the real paths (`blocks.1.alt`)
     * and the real sentences, which is what `routeTemplateIssues` was built to
     * place. The request the arm accepts is a member of the union, so the wire
     * shape is unchanged.
     *
     * The API still parses the union, so a body that gets past this check and is
     * refused there still arrives as "Invalid input" in `error.fieldErrors`.
     * That is the server's half of the same defect and it is not fixed here.
     */
    const parsed = updateEmailTemplateRequestSchema.safeParse(request);
    if (!parsed.success) {
      setSaveIssues(routeTemplateIssues(fieldErrorsFromZod(parsed.error), request.blocks));
      return;
    }

    setSaveIssues(NO_TEMPLATE_ISSUES);
    setSaving(true);

    try {
      const saved = await updateTemplate(template.id, parsed.data);
      setTemplate(saved);
      setDraft(draftOf(saved));
      setAssets(saved.assets);
      toast({ tone: 'success', title: 'Template saved', description: 'It was recompiled as it was stored.' });
    } catch (error) {
      setSaveIssues(routeTemplateIssues(isApiError(error) ? error.fieldErrors : [], request.blocks));
      setSaveFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
    } finally {
      setSaving(false);
    }
  }

  async function onSetStatus(status: 'draft' | 'approved'): Promise<void> {
    if (changingStatus) return;

    setSaveFailure(null);
    setChangingStatus(true);

    try {
      const updated = await setTemplateStatus(template.id, { status });
      setTemplate(updated);
      setDraft(draftOf(updated));
      toast({
        tone: 'success',
        title: status === 'approved' ? 'Template approved' : 'Back to draft',
        description:
          status === 'approved'
            ? 'A campaign can now use this template.'
            : 'A campaign cannot pick it while it is a draft.',
      });
    } catch (error) {
      // The refusal names every outstanding blocker, keyed by which one it is.
      setSaveIssues(
        routeTemplateIssues(isApiError(error) ? error.fieldErrors : [], currentDocument),
      );
      setSaveFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
    } finally {
      setChangingStatus(false);
    }
  }

  const approved = template.status === 'approved';

  return (
    <AppLayout
      title={
        <span className="flex flex-wrap items-center gap-3">
          <span className="min-w-0 truncate">{template.name}</span>
          <TemplateStatusBadge status={template.status} />
          {dirty && (
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-warn">
              unsaved changes
            </span>
          )}
        </span>
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <BackToTemplates />

          <Button
            type="button"
            variant={approved ? 'ghost' : 'secondary'}
            size="sm"
            loading={changingStatus}
            disabled={saving || (!approved && dirty)}
            leadingIcon={
              approved ? <Undo2 aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />
            }
            onClick={() => {
              void onSetStatus(approved ? 'draft' : 'approved');
            }}
          >
            {approved ? 'Back to draft' : 'Approve'}
          </Button>

          <Button
            type="button"
            size="sm"
            loading={saving}
            // Enabled with no edits when saving would publish a picture — see the
            // header. Otherwise exactly as before: nothing to save, nothing to press.
            disabled={(!dirty && !needsPublishing) || changingStatus}
            leadingIcon={<Save aria-hidden="true" />}
            onClick={() => {
              void onSave();
            }}
          >
            {dirty
              ? 'Save changes'
              : needsPicturePublishing
                ? 'Save to publish pictures'
                : needsFilePublishing
                  ? 'Save to publish files'
                  : 'Saved'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {saveFailure !== null && (
          <FormAlert
            tone="error"
            {...(saveFailure.requestId === undefined ? {} : { requestId: saveFailure.requestId })}
          >
            {saveFailure.message}
          </FormAlert>
        )}

        {/*
          Anything the API refused that could not be pinned on one block. It sits
          above the tabs rather than inside one, because a message about the
          document as a whole — the footer is missing, an image is not uploaded
          to this template — belongs wherever you are standing when it arrives.
        */}
        {issues.general.length > 0 && (
          <Callout tone="bad" title="This template was refused" live="polite">
            <ul className="flex list-none flex-col gap-1.5">
              {issues.general.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Callout>
        )}

        {/*
          The campaign refusal's own sentence, where its instruction can be carried
          out. Above the tabs, beside the Save button it names, and a warning
          rather than an error: nothing was refused here, and a save fixes it.
        */}
        {needsPicturePublishing && (
          <Callout tone="warn" title="Pictures not yet published">
            {templateImagesUnpublishedMessage(imagePublishing.unpublished, imagePublishing.referenced)}
          </Callout>
        )}

        {/*
          The file version of the same thing, in its own callout rather than
          folded into the one above: the two are fixed by the same press but they
          are different files, and a person holding one fault should not have to
          read about the other kind to find out which of theirs it is. Slice 23.
        */}
        {needsFilePublishing && (
          <Callout tone="warn" title="Files not yet published">
            {templateAttachmentsUnpublishedMessage(
              attachmentPublishing.unpublished,
              attachmentPublishing.total,
            )}
          </Callout>
        )}

        {!approved && dirty && (
          <p className="text-[0.85rem] text-muted">
            Approval checks the saved template, so save before approving.
          </p>
        )}

        {/*
          Kept whole and kept visible. Editing an approved template silently
          changes what an already-built campaign will send, and there is no
          second screen that says so — a person who draws the opposite conclusion
          here finds out when the campaign goes out.
        */}
        {approved && (
          <FormAlert tone="info">
            {TEMPLATE_STATUS_DISPLAY.approved.meaning} Editing it changes what any campaign that
            has not sent yet will send.
          </FormAlert>
        )}

        {/*
          `keepMounted` matters here rather than being a preference: the preview
          panel holds an <iframe>, and unmounting it on every tab switch would
          throw the rendered email away and reload it from `srcdoc` each time.
        */}
        <Tabs defaultValue="edit" keepMounted>
          <TabList label="Template">
            <Tab value="edit">
              <PencilLine aria-hidden="true" />
              Edit
            </Tab>
            <Tab value="preview">
              <Eye aria-hidden="true" />
              Preview
            </Tab>
          </TabList>

          <TabPanel value="edit" tabIndex={-1}>
            <div className="flex flex-col gap-5">
              <SettingsCard
                draft={draft}
                issues={issues}
                disabled={saving}
                onChange={edit}
              />

              {/*
                What a recipient actually receives, above the blocks rather than
                below them: it is a fact about every message this template will
                ever send, and somebody deciding whether to put a link in one
                needs it before they write the link, not after.
              */}
              {carriesNotice !== null && <RecipientTrackingNotice notice={carriesNotice} />}

              <VariableHelp />

              <BlockEditor
                templateId={template.id}
                body={draft.body}
                footer={draft.footer}
                assets={assets}
                imageDelivery={template.imageDelivery}
                issues={issues}
                tracking={tracking}
                disabled={saving}
                onChangeBody={(body) => {
                  edit({ body });
                }}
                onChangeFooter={(footer) => {
                  edit({ footer });
                }}
                onAssetUploaded={(asset) => {
                  setAssets((current) => [...current, asset]);
                }}
              />

              <AttachmentsPanel
                templateId={template.id}
                assets={assets}
                imageDelivery={template.imageDelivery}
                disabled={saving}
                onUploaded={(asset) => {
                  setAssets((current) => [...current, asset]);
                }}
                onRemoved={(assetId) => {
                  setAssets((current) => current.filter((asset) => asset.id !== assetId));
                }}
              />

              <TestSendPanel template={template} tracking={tracking} disabled={saving} />

              <DeletePanel
                template={template}
                onDeleted={() => {
                  navigate('/templates');
                }}
              />
            </div>
          </TabPanel>

          <TabPanel value="preview" tabIndex={-1}>
            <TemplatePreview
              preview={preview.data?.response ?? null}
              storedHtml={template.htmlCompiled}
              dirty={dirty}
              substitute={substitute}
              onSubstituteChange={setSubstitute}
              loading={preview.status === 'loading'}
              error={preview.error}
              hasImages={draft.body.some((block) => block.type === 'image')}
              // Not `needsFilePublishing`: this is about what the sent message
              // carries, which is every file on the template, published or not.
              addsAttachmentLinks={attachmentPublishing.total > 0}
              tracking={tracking}
              // The settings the displayed HTML was compiled for, kept with the
              // answer — never the pending request's, which would redraw
              // the old HTML in a frame that describes the new.
              bodyWidth={preview.data?.bodyWidth ?? template.bodyWidth}
              background={preview.data?.background ?? template.background}
            />
          </TabPanel>
        </Tabs>

        <p className="font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted">
          Compiled{' '}
          {template.compiledAt === null ? 'never' : formatDateTime(template.compiledAt)} · edited{' '}
          {formatDateTime(template.updatedAt)}
        </p>
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Settings                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Everything about the email that is not a block.
 *
 * The subject sits here rather than in the block list because it is not part of
 * the document — it is a column on the template, it is what an inbox shows before
 * anything is opened, and it is one of the four things approval refuses a
 * template for lacking.
 *
 * Width and background (slice 56) sit here for the same reason: they are
 * columns on the template, about the whole email, and a per-block version of
 * either was ruled out. Two `Select`s with labels and nothing else.
 */
function SettingsCard({
  draft,
  issues,
  disabled,
  onChange,
}: {
  draft: Draft;
  issues: TemplateIssues;
  disabled: boolean;
  onChange: (change: Partial<Draft>) => void;
}) {
  const first = (field: string): string | undefined => fieldIssues(issues, field)[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1rem]">Email settings</CardTitle>
      </CardHeader>

      <CardContent className="grid gap-4 sm:grid-cols-2">
        <FormField label="Template name" required error={first('name')}>
          <Input
            value={draft.name}
            disabled={disabled}
            onChange={(event) => {
              onChange({ name: event.target.value });
            }}
          />
        </FormField>

        <FormField
          label="Reply-To"
          description="Where replies go, if not the sending address. Optional."
          error={first('replyTo')}
        >
          <Input
            type="email"
            value={draft.replyTo}
            spellCheck={false}
            placeholder="hello@acmeretail.in"
            disabled={disabled}
            onChange={(event) => {
              onChange({ replyTo: event.target.value });
            }}
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField label="Subject line" error={first('subject')}>
            <Input
              value={draft.subject}
              disabled={disabled}
              placeholder="Your Diwali offer is here, {{ name }}"
              onChange={(event) => {
                onChange({ subject: event.target.value });
              }}
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField
            label="Preheader"
            description="Optional. The grey line beside the subject; left empty, clients use the first words of the email."
            error={first('preheader')}
          >
            <Textarea
              rows={2}
              value={draft.preheader}
              disabled={disabled}
              onChange={(event) => {
                onChange({ preheader: event.target.value });
              }}
            />
          </FormField>
        </div>

        <FormField label="Width" error={first('bodyWidth')}>
          <Select
            value={draft.bodyWidth}
            options={TEMPLATE_BODY_WIDTH_OPTIONS}
            disabled={disabled}
            size="sm"
            onValueChange={(next) => {
              if (isTemplateBodyWidth(next)) onChange({ bodyWidth: next });
            }}
          />
        </FormField>

        <FormField label="Background" error={first('background')}>
          <Select
            value={draft.background}
            options={TEMPLATE_BACKGROUND_OPTIONS}
            disabled={disabled}
            size="sm"
            onValueChange={(next) => {
              if (isTemplateBackground(next)) onChange({ background: next });
            }}
          />
        </FormField>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Deleting                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Deleting, confirmed inline rather than in a dialog — the same way disconnecting
 * SES and deleting a segment do it, and for the same reason: what a confirmation
 * has to say is not "are you sure" but *what is lost*.
 */
function DeletePanel({ template, onDeleted }: { template: Template; onDeleted: () => void }) {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  /**
   * The refusal, with the request id when the API sent one.
   *
   * It was a bare string until slice 17, which is the one failure on this screen
   * a person cannot work around by trying something else — a delete that will not
   * go through needs somebody to look in the log, and the id is how the line is
   * found. Every other verdict on this page already carried it.
   */
  const [failure, setFailure] = useState<{
    message: string;
    requestId: string | undefined;
  } | null>(null);

  async function onDelete(): Promise<void> {
    if (pending) return;

    setFailure(null);
    setPending(true);

    try {
      await deleteTemplate(template.id);
      toast({ tone: 'success', title: 'Template deleted' });
      onDeleted();
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
      setPending(false);
    }
  }

  return (
    <Card className="border-bad/25">
      <CardHeader>
        <CardTitle className="text-[1rem]">Delete this template</CardTitle>
        <CardDescription>
          The document, the compiled email and every file uploaded to it go together. Cannot be
          undone. Your lists, segments and contacts are untouched.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failure !== null && (
          <FormAlert
            tone="error"
            {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
          >
            {failure.message}
          </FormAlert>
        )}

        {confirming ? (
          <div className="flex flex-col gap-3">
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
              Delete <span className="font-medium">{template.name}</span>, its compiled email and
              its uploads? This cannot be undone.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={pending}
                className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
                leadingIcon={<Trash2 aria-hidden="true" />}
                onClick={() => {
                  void onDelete();
                }}
              >
                {pending ? 'Deleting' : 'Yes, delete it'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Keep it
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<Trash2 aria-hidden="true" />}
              onClick={() => {
                setConfirming(true);
              }}
            >
              Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
